import * as Sentry from '@sentry/vue'

/**
 * Standard checkout error levels
 */
export const ErrorLevel = {
	ERROR: 'error', // Critical failures that block checkout
	WARNING: 'warning', // Non-blocking issues (e.g., post-order status update failures)
	INFO: 'info', // Informational events
}

/**
 * Checkout step identifiers for consistent tagging
 */
export const CheckoutStep = {
	CART: 'cart',
	CART_VALIDATION: 'cart_validation',
	SHIPPING: 'shipping',
	BILLING: 'billing',
	PAYMENT: 'payment',
	ORDER_PLACEMENT: 'order_placement',
	ORDER_CONFIRMATION: 'order_confirmation',
}

/**
 * Action identifiers for consistent tagging
 */
export const CheckoutAction = {
	// Cart operations
	SAVE_CART: 'saveCart',
	SYNC_CART: 'syncCart',
	PROCESS_CART: 'processCart',
	ADD_TO_CART: 'addToCart',
	UPDATE_CART_ITEM: 'updateCartItem',
	REMOVE_FROM_CART: 'removeFromCart',

	// Cart validation
	FETCH_PRODUCTS: 'fetchProducts',
	FETCH_VARIANTS: 'fetchVariants',
	VALIDATE_VARIANTS: 'validateVariants',

	// Shipping
	GET_SHIPPING_ESTIMATES: 'getShippingEstimates',
	UPDATE_SHIPPING_ADDRESS: 'updateShippingAddress',
	SELECT_SHIPPING_METHOD: 'selectShippingMethod',

	// Addresses
	FETCH_ADDRESSES: 'fetchAddresses',
	FETCH_BILLING_ADDRESSES: 'fetchBillingAddresses',
	SAVE_ADDRESS: 'saveAddress',

	// Checkout
	FETCH_BC_CHECKOUT: 'fetchBCCheckout',
	VALIDATE_CONSIGNMENT: 'validateConsignment',
	VALIDATE_CHECKOUT: 'validateCheckout',

	// Payment
	GET_CLIENT_TOKEN: 'getClientToken',
	TOKENIZE_PAYMENT: 'tokenizePayment',
	PROCESS_BRAINTREE_PAYMENT: 'processBraintreePayment',
	PLACE_ORDER: 'placeOrder',

	// Post-order
	UPDATE_ORDER_STATUS: 'updateOrderStatus',
	FETCH_GUEST_ORDER: 'fetchGuestOrder',
}

/** Checkout funnel steps for Sentry breadcrumb dashboards (filter category: checkout.funnel). */
export const CheckoutFunnelStep = {
	VALIDATE_CHECKOUT: 'validate_checkout',
	VALIDATE_CONSIGNMENT: 'validate_consignment',
	PAYMENT_SUCCESS: 'payment_success',
}

/**
 * Coarse device family for funnel breakdowns (mirrors Sentry device.family grouping).
 */
export function getCheckoutDeviceFamily() {
	const ua = navigator.userAgent || ''
	if (/Instagram/i.test(ua)) return 'Instagram'
	if (/FBAN|FBAV|Facebook/i.test(ua)) return 'Facebook'
	if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone'
	if (/Android/i.test(ua)) return 'Android'
	if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac'
	if (/Windows/i.test(ua)) return 'Windows'
	if (/CrOS/i.test(ua)) return 'Chrome OS'
	return 'Other'
}

export function isInAppBrowser() {
	const family = getCheckoutDeviceFamily()
	return family === 'Instagram' || family === 'Facebook'
}

/**
 * Record a checkout funnel breadcrumb with device.family-style tagging for dashboards.
 */
export function trackCheckoutFunnel(step, data = {}) {
	const deviceFamily = getCheckoutDeviceFamily()
	const inApp = isInAppBrowser()

	Sentry.setTag('checkout.deviceFamily', deviceFamily)
	if (inApp) {
		Sentry.setTag('checkout.inAppBrowser', deviceFamily)
	}

	Sentry.addBreadcrumb({
		message: `Checkout funnel: ${step}`,
		category: 'checkout.funnel',
		level: 'info',
		data: {
			funnelStep: step,
			deviceFamily,
			isInAppBrowser: inApp,
			...data,
		},
	})
}

/**
 * Track shipping/state auto-recovery outcomes (success rate in Sentry via syncBug + autoRecoveryOutcome).
 */
export function trackAutoRecoveryOutcome(outcome, extra = {}) {
	const deviceFamily = getCheckoutDeviceFamily()
	const syncBug = extra.syncBug || 'SHIPPING_ADDRESS_STATE_SYNC_BUG'

	Sentry.addBreadcrumb({
		message: `Checkout auto-recovery: ${outcome}`,
		category: 'checkout.auto_recovery',
		level: outcome === 'success' ? 'info' : 'warning',
		data: {
			autoRecoveryOutcome: outcome,
			syncBug,
			deviceFamily,
			...extra,
		},
	})
}

/**
 * Check if we should log to Sentry (skip in development)
 */
function shouldLog() {
	return process.env.NODE_ENV !== 'development'
}

/**
 * Backend failure codes that are always system errors — never skip Sentry.
 * Checked before message patterns so BC 4xx create-order failures still alert.
 */
const SYSTEM_FAILURE_CODES = new Set([
	'CREATE_ORDER_FAILED',
	'UPDATE_CHECKOUT_FAILED',
	'UPDATE_ORDER_FAILED',
	'PROCESS_PAYMENT_FAILED',
	'READ_ORDER_FAILED',
	'CHECKOUT_TIMEOUT',
	'CHECKOUT_JOB_FAILED',
	'MISSING_CHECKOUT_ID',
	'PROCESS_STORE_CREDIT_FAILED',
	'PROCESS_GIFT_CERTIFICATE_FAILED',
])

/** Codes that are expected / shopper-recoverable — skip Sentry (unless force). */
const USER_FIXABLE_CODES = new Set(['CREATE_ORDER_STOCK_FAILED', 'ACH_NOT_AUTHENTICATED'])

/**
 * User-fixable payment error message patterns (declines, instrument validation).
 * Keep these specific — broad tokens like "invalid" / "try again" hide real system failures.
 */
const USER_FIXABLE_PATTERNS = [
	'card details',
	'could not be verified',
	'card has expired',
	'card issuer',
	'declined',
	'do not honor',
	'insufficient funds',
	'routing_number',
	'routing number',
	'account number',
	'invalid bank',
	'bank account',
	'unable to verify your account',
	'failed to vault',
	'cvv',
	'cvc',
	'verification failed',
	'processor declined',
	'contact your card issuer',
	'contact your bank',
	'duplicate transaction',
	'missing card information',
	'missing stored card information',
	'missing ach information',
	'missing stored ach information',
	'missing email address',
	'missing shipping information',
	'missing billing information',
	'no shipping method selected',
	'please select a valid ach',
	'please provide the following ach',
]

/**
 * Resolve backend checkout failure code from response / error payloads.
 */
export function resolveCheckoutFailureCode(errorData) {
	if (!errorData) return null
	const code =
		errorData.code ??
		errorData?.data?.code ??
		errorData?.response?.data?.code ??
		errorData?.response?.code ??
		null
	return typeof code === 'string' && code.length ? code : null
}

function isSystemFailureCode(code) {
	if (!code || typeof code !== 'string') return false
	return SYSTEM_FAILURE_CODES.has(code)
}

function isCheckoutPipelineFailureCode(code) {
	return typeof code === 'string' && /_CHECKOUT_FAILED$/i.test(code)
}

/**
 * Resolve BC order id from error payload, API response, checkout store, or extras.
 */
function resolveBcOrderId({ error, extra = {}, checkout, response } = {}) {
	return (
		extra.bcOrderId ??
		checkout?.bcOrderId ??
		response?.bcOrderId ??
		response?.data?.bcOrderId ??
		error?.response?.data?.bcOrderId ??
		null
	)
}

function applyBcOrderIdTag(scope, bcOrderId) {
	if (bcOrderId != null && bcOrderId !== '') {
		scope.setTag('bcOrderId', String(bcOrderId))
	}
}

function paymentProcessorForMethod(paymentMethod) {
	switch (paymentMethod) {
		case 'ach':
		case 'card':
		case 'stored_card':
		case 'paypal':
		case 'paylater':
		case 'venmo':
		case 'apple_pay':
		case 'google_pay':
			return 'braintree'
		case 'net30':
			return 'net30'
		case 'credit':
			return 'store_credit'
		default:
			return paymentMethod || 'unknown'
	}
}

function defaultPaymentAction(paymentMethod) {
	if (paymentMethod === 'braintree') {
		return CheckoutAction.PROCESS_BRAINTREE_PAYMENT
	}
	return CheckoutAction.PLACE_ORDER
}

/**
 * Classify payment failures as user-fixable (warning) vs system errors.
 * Prefer backend `code` over HTTP status / broad message substrings.
 */
export function getPaymentErrorLevel(errorData) {
	const code = resolveCheckoutFailureCode(errorData)
	const message = (
		errorData?.message ||
		errorData?.response?.data?.message ||
		errorData?.response?.message ||
		''
	).toLowerCase()

	if (code && USER_FIXABLE_CODES.has(code)) return ErrorLevel.WARNING

	// Explicit system codes always error — even when BC returns 4xx or the message contains "invalid".
	if (isSystemFailureCode(code)) return ErrorLevel.ERROR

	const isUserFixableMessage = USER_FIXABLE_PATTERNS.some((pattern) => message.includes(pattern))
	if (isUserFixableMessage) return ErrorLevel.WARNING

	// CARD_CHECKOUT_FAILED / ACH_CHECKOUT_FAILED / etc. after message filter = real pipeline failure.
	if (isCheckoutPipelineFailureCode(code)) return ErrorLevel.ERROR

	// PROCESS_CARD_PAYMENT_FAILED / PROCESS_BRAINTREE_PAYMENT_FAILED: declines matched above;
	// unexpected gateway failures fall through to error.
	return ErrorLevel.ERROR
}

const CARD_GATEWAY_CODES = new Set(['PROCESS_CARD_PAYMENT_FAILED', 'PROCESS_BRAINTREE_PAYMENT_FAILED'])

/**
 * Decline copy that getPaymentErrorLevel's shopper-message list does not already catch.
 * Kept specific so timeouts and pipeline failures stay errors.
 */
const ORDINARY_CARD_DECLINE_PATTERNS = [
	'gateway rejected',
	'call issuer',
	'refer to issuer',
	'pick up card',
	'pickup card',
	'lost card',
	'stolen card',
	'restricted card',
	'soft decline',
	'hard decline',
	'transaction not permitted',
	'transaction not allowed',
	'card not supported',
	'exceeds withdrawal',
	'activity limit',
	'security violation',
	'avs',
]

function paymentFailureText(payload) {
	if (!payload || typeof payload !== 'object') return ''
	const parts = []
	const push = (value) => {
		if (typeof value === 'string' && value) parts.push(value)
	}
	push(payload.message)
	push(payload.title)
	push(payload.reason)
	push(payload.giacMessage)
	push(payload.response?.data?.message)
	push(payload.response?.data?.title)
	const errors = payload.errors ?? payload.response?.data?.errors
	if (typeof errors === 'string') push(errors)
	else if (Array.isArray(errors)) {
		for (const item of errors) {
			if (typeof item === 'string') push(item)
			else if (item && typeof item === 'object') {
				push(item.message)
				push(item.code)
			}
		}
	} else if (errors && typeof errors === 'object') {
		for (const value of Object.values(errors)) push(typeof value === 'string' ? value : '')
	}
	return parts.join(' ').toLowerCase()
}

function paymentHttpStatus(payload) {
	if (!payload || typeof payload !== 'object') return null
	const candidates = [payload.status, payload.httpStatus, payload.response?.status, payload.response?.data?.status]
	for (const value of candidates) {
		const status = Number(value)
		if (Number.isInteger(status) && status >= 100 && status < 600) return status
	}
	return null
}

/**
 * Ordinary issuer/instrument declines that still fall through getPaymentErrorLevel as errors
 * (generic PROCESS_CARD_PAYMENT_FAILED copy, or a 4xx from the payments API).
 */
export function isOrdinaryCardDecline(payload) {
	const code = resolveCheckoutFailureCode(payload)
	if (isSystemFailureCode(code) || code === 'CHECKOUT_JOB_FAILED') return false
	if (isCheckoutPipelineFailureCode(code)) return false

	const text = paymentFailureText(payload)
	if (ORDINARY_CARD_DECLINE_PATTERNS.some((pattern) => text.includes(pattern))) return true
	if (USER_FIXABLE_PATTERNS.some((pattern) => text.includes(pattern))) return true

	// A 4xx from the card gateway with no specific reason is the usual issuer decline.
	// Named configuration/pipeline failures stay errors even when the status is 4xx.
	const status = paymentHttpStatus(payload)
	const genericCardMessages = new Set([
		'',
		'error processing card payment',
		'#1.01 error processing payment',
		'unknown error processing braintree payment',
	])
	return Boolean(
		code && CARD_GATEWAY_CODES.has(code) && genericCardMessages.has(text.trim()) && status != null && status >= 400 && status < 500,
	)
}

/**
 * Level to report for a checkout payment failure.
 * Expected card declines that would have been errors are warnings. Callers still send those
 * warnings; other warnings stay on the existing "skip expected user error" path.
 */
export function cardPaymentReportLevel(message, payload, explicitLevel = null) {
	const classifiedLevel = explicitLevel ?? getPaymentErrorLevel(payload)
	if (
		explicitLevel == null &&
		message === 'Card payment failed' &&
		classifiedLevel === ErrorLevel.ERROR &&
		isOrdinaryCardDecline(payload)
	) {
		return { level: ErrorLevel.WARNING, downgraded: true }
	}
	return { level: classifiedLevel, downgraded: false }
}

/**
 * Build standard context object for checkout errors
 * @param {Object} options - Context options
 * @returns {Object} Sanitized context object
 */
function buildContext(options = {}) {
	const { store, checkout, extra = {} } = options

	return {
		// Cart identifiers
		mangoCart: store?.cart?.id || null,
		bcCart: store?.cart?.bcId || null,
		cartItemsCount: store?.cart?.cartItems?.length || 0,

		// User identifiers
		userId: store?.user?.id || null,
		userEmail: store?.user?.email || checkout?.guest?.email || null,
		isGuest: !store?.user?.id,

		// Checkout state (if available)
		...(checkout && {
			shippingAddress: checkout.shipping?.shippingAddress || null,
			billingAddress: checkout.billing?.billingAddress || null,
			selectedShipping: checkout.shipping?.selectedShipping?.id || null,
			consignmentId: checkout.shipping?.consignmentId || null,
			grandTotal: checkout.grandTotal || null,
		}),

		// Additional context
		...extra,
	}
}

/**
 * Capture a checkout-related error with standardized context
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.component - Component name (e.g., 'checkout', 'ShippingAndBilling')
 * @param {string} options.action - Action that failed (use CheckoutAction constants)
 * @param {string} options.step - Checkout step (use CheckoutStep constants)
 * @param {string} options.level - Error level (use ErrorLevel constants, defaults to ERROR)
 * @param {Object} options.store - Store instance for context
 * @param {Object} options.checkout - Checkout store for context
 * @param {Object} options.extra - Additional context specific to this error
 * @param {Object} options.tags - Additional tags specific to this error
 */
export function captureCheckoutError(error, options = {}) {
	if (!shouldLog() && !options.force) {
		console.error(`[${options.component}:${options.action}]`, error)
		return
	}

	const { component, action, step, level = ErrorLevel.ERROR, store, checkout, extra = {}, tags = {} } = options

	const bcOrderId = resolveBcOrderId({ error, extra, checkout })
	const context = buildContext({
		store,
		checkout,
		extra: {
			...(bcOrderId != null ? { bcOrderId } : {}),
			...extra,
		},
	})

	Sentry.withScope((scope) => {
		// Standard tags
		scope.setTag('component', component || 'unknown')
		scope.setTag('action', action || 'unknown')
		if (step) scope.setTag('checkout.step', step)
		scope.setTag('error.type', 'checkout')

		// Cart/user identification tags
		if (context.mangoCart) scope.setTag('mangoCart', context.mangoCart)
		if (context.bcCart) scope.setTag('bcCart', context.bcCart)
		if (context.userId) scope.setTag('userId', context.userId)
		scope.setTag('isGuest', context.isGuest ? 'true' : 'false')
		applyBcOrderIdTag(scope, bcOrderId)

		// Additional custom tags
		Object.entries(tags).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				scope.setTag(key, String(value))
			}
		})

		// Set context as extras
		scope.setContext('checkout', context)

		// Error-specific extras
		if (error?.response?.data) {
			scope.setExtra('errorResponse', error.response.data)
		}
		if (error?.response?.status) {
			scope.setExtra('httpStatus', error.response.status)
		}
		if (error?.message) {
			scope.setExtra('errorMessage', error.message)
		}

		// Set level
		scope.setLevel(level)

		// Capture
		if (error instanceof Error) {
			Sentry.captureException(error)
		} else {
			Sentry.captureMessage(typeof error === 'string' ? error : JSON.stringify(error))
		}
	})
}

/**
 * Capture a payment-specific error
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.paymentMethod - Payment method (e.g., 'card', 'ach', 'paypal', 'venmo', 'net30')
 * @param {string} options.processor - Payment processor (e.g., 'braintree', 'stripe')
 * @param {string} options.bcOrderId - BigCommerce order ID if available
 * @param {string} options.level - Error level (defaults to ERROR)
 * @param {Object} options.store - Store instance for context
 * @param {Object} options.checkout - Checkout store for context
 * @param {Object} options.extra - Additional context
 */
export function capturePaymentError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Payment:${options.paymentMethod}]`, error)
		return
	}

	const {
		paymentMethod,
		processor,
		bcOrderId: bcOrderIdOption,
		level = ErrorLevel.ERROR,
		store,
		checkout,
		action,
		extra = {},
	} = options

	const resolvedBcOrderId = bcOrderIdOption ?? resolveBcOrderId({ error, extra, checkout, response: extra.response })
	const resolvedProcessor = processor ?? paymentProcessorForMethod(paymentMethod)
	const resolvedAction = action ?? defaultPaymentAction(paymentMethod)
	const failureCode =
		resolveCheckoutFailureCode(extra) ||
		resolveCheckoutFailureCode(extra.response) ||
		resolveCheckoutFailureCode(error?.response?.data) ||
		resolveCheckoutFailureCode(error)

	captureCheckoutError(error, {
		component: 'payment',
		action: resolvedAction,
		step: CheckoutStep.PAYMENT,
		level,
		store,
		checkout,
		tags: {
			'payment.method': paymentMethod,
			'payment.processor': resolvedProcessor,
			...(failureCode ? { 'checkout.failureCode': failureCode } : {}),
		},
		extra: {
			paymentMethod,
			processor: resolvedProcessor,
			bcOrderId: resolvedBcOrderId,
			...(failureCode ? { failureCode } : {}),
			...extra,
		},
	})
}

/**
 * Log checkout payment failures (card, ACH, wallets, NET30) with consistent context.
 * Skips Sentry for expected user-fixable errors unless `force` is true.
 */
export function logCheckoutPaymentFailure(options = {}) {
	const {
		message,
		paymentMethod = 'unknown',
		store,
		checkout,
		response,
		error = null,
		action,
		bcOrderId: bcOrderIdOption,
		extra = {},
		force = false,
		level = null,
	} = options

	const responseData = response?.data ?? response ?? error?.response?.data ?? null
	const resolvedBcOrderId = bcOrderIdOption ?? resolveBcOrderId({ error, checkout, response: responseData, extra })
	const failureCode =
		resolveCheckoutFailureCode(responseData) ||
		resolveCheckoutFailureCode(extra) ||
		resolveCheckoutFailureCode(error?.response?.data) ||
		resolveCheckoutFailureCode(error)
	const classifyPayload = responseData || error || extra
	const declinePayload =
		classifyPayload && typeof classifyPayload === 'object'
			? { ...classifyPayload, httpStatus: extra?.httpStatus ?? classifyPayload.httpStatus ?? classifyPayload.status }
			: classifyPayload
	const { level: errorLevel, downgraded: downgradedCardDecline } = cardPaymentReportLevel(message, declinePayload, level)
	const errorMessage = message || responseData?.message || error?.message || 'Checkout payment failed'

	if (!force && PAYMENT_NOISE_PATTERNS.some((pattern) => pattern.test(errorMessage))) {
		return
	}

	// Expected shopper mistakes stay out of Sentry. Card declines that used to be grouped as
	// the error "Card payment failed" are the exception: send them at warning.
	if (!force && errorLevel === ErrorLevel.WARNING && !downgradedCardDecline) {
		console.log('[Payment] Skipping Sentry log for expected user error:', errorMessage, failureCode || '')
		return
	}

	if (!shouldLog() && !force) {
		console.error(`[Payment:${paymentMethod}]`, errorMessage, responseData || error)
		return
	}

	const payload = error instanceof Error ? error : errorMessage

	let serializableResponse = null
	const rawResponse = responseData ?? response ?? null
	if (rawResponse) {
		try {
			serializableResponse = JSON.parse(
				JSON.stringify(rawResponse, (_key, value) => {
					if (value instanceof Error) return value.message
					return value
				}),
			)
		} catch {
			serializableResponse = { message: rawResponse?.message || String(rawResponse) }
		}
	}

	capturePaymentError(payload, {
		paymentMethod,
		bcOrderId: resolvedBcOrderId,
		action: action ?? defaultPaymentAction(paymentMethod),
		level: errorLevel,
		store,
		checkout,
		extra: {
			...extra,
			code: failureCode ?? responseData?.code ?? extra.code,
			response: serializableResponse,
			errorDetails: error?.message ?? (typeof extra.errorDetails === 'string' ? extra.errorDetails : null),
		},
	})
}

/**
 * Capture a cart validation error
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.action - Validation action (e.g., 'fetchProducts', 'validateVariants')
 * @param {Object} options.store - Store instance
 * @param {Array} options.productIds - Product IDs being validated
 * @param {Array} options.variantIds - Variant IDs being validated
 * @param {number} options.cartItemsCount - Number of items in cart
 * @param {Object} options.extra - Additional context
 */
export function captureValidationError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Validation:${options.action}]`, error)
		return
	}

	const { action, store, productIds, variantIds, cartItemsCount, extra = {} } = options

	captureCheckoutError(error, {
		component: 'validateCart',
		action,
		step: CheckoutStep.CART_VALIDATION,
		level: ErrorLevel.ERROR,
		store,
		extra: {
			productIds,
			variantIds,
			cartItemsCount,
			...extra,
		},
	})
}

/**
 * Capture an address-related error
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.action - Address action (e.g., 'fetchAddresses', 'saveAddress')
 * @param {string} options.addressType - 'shipping' or 'billing'
 * @param {Object} options.store - Store instance
 * @param {Object} options.checkout - Checkout store
 * @param {Object} options.extra - Additional context
 */
export function captureAddressError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Address:${options.action}]`, error)
		return
	}

	const { action, addressType, store, checkout, extra = {} } = options

	const step = addressType === 'billing' ? CheckoutStep.BILLING : CheckoutStep.SHIPPING

	captureCheckoutError(error, {
		component: 'ShippingAndBilling',
		action,
		step,
		level: ErrorLevel.ERROR,
		store,
		checkout,
		tags: {
			addressType,
		},
		extra: {
			addressType,
			...extra,
		},
	})
}

/**
 * Capture a shipping-related error
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.action - Shipping action
 * @param {Object} options.store - Store instance
 * @param {Object} options.checkout - Checkout store
 * @param {Object} options.consignment - Consignment data being sent
 * @param {Object} options.extra - Additional context
 */
export function captureShippingError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Shipping:${options.action}]`, error)
		return
	}

	const { action, store, checkout, consignment, extra = {} } = options

	captureCheckoutError(error, {
		component: 'checkout',
		action,
		step: CheckoutStep.SHIPPING,
		level: ErrorLevel.ERROR,
		store,
		checkout,
		extra: {
			consignment: consignment
				? {
						bcCartId: consignment.bcCartId,
						hasAddress: !!consignment.address,
					}
				: null,
			...extra,
		},
	})
}

/**
 * Capture an order placement error
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.action - Order action
 * @param {string} options.bcOrderId - BigCommerce order ID if available
 * @param {string} options.paymentMethod - Payment method used
 * @param {Object} options.store - Store instance
 * @param {Object} options.checkout - Checkout store
 * @param {Object} options.extra - Additional context
 */
export function captureOrderError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Order:${options.action}]`, error)
		return
	}

	const { action, bcOrderId, paymentMethod, level = ErrorLevel.ERROR, store, checkout, extra = {} } = options

	captureCheckoutError(error, {
		component: 'checkout',
		action,
		step: CheckoutStep.ORDER_PLACEMENT,
		level,
		store,
		checkout,
		tags: {
			bcOrderId,
			paymentMethod,
		},
		extra: {
			bcOrderId,
			paymentMethod,
			...extra,
		},
	})
}

/**
 * Capture a cart-related error (add to cart, save cart, sync cart failures)
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {string} options.action - Cart action (use CheckoutAction constants)
 * @param {string} options.level - Error level (defaults to ERROR)
 * @param {Object} options.cart - Cart object for context
 * @param {Object} options.user - User object for context
 * @param {Array} options.warnings - Warning messages from backend
 * @param {Object} options.response - API response data
 * @param {Object} options.extra - Additional context
 */
export function captureCartError(error, options = {}) {
	if (!shouldLog()) {
		console.error(`[Cart:${options.action}]`, error)
		return
	}

	const { action, level = ErrorLevel.ERROR, cart, user, warnings, response, extra = {} } = options
	const messageText = typeof error === 'string' ? error : warnings ? (Array.isArray(warnings) ? warnings.join(' ') : String(warnings)) : ''
	if (shouldSkipCartSentryCapture(messageText)) {
		return
	}

	Sentry.withScope((scope) => {
		// Standard tags
		scope.setTag('component', 'cart')
		scope.setTag('action', action || 'unknown')
		scope.setTag('checkout.step', CheckoutStep.CART)
		scope.setTag('error.type', 'cart')

		// Cart/user identification tags
		if (cart?.id) scope.setTag('mangoCart', cart.id)
		if (cart?.bcId) scope.setTag('bcCart', cart.bcId)
		if (user?.id) scope.setTag('userId', user.id)
		scope.setTag('isGuest', !user?.id ? 'true' : 'false')

		// Set context
		scope.setContext('cart', {
			mangoCartId: cart?.id || null,
			bcCartId: cart?.bcId || null,
			cartItemsCount: cart?.cartItems?.length || 0,
			certificatesCount: cart?.certificates?.length || 0,
			hasCoupon: !!cart?.coupon?.code,
			userId: user?.id || null,
			userEmail: user?.email || null,
			isGuest: !user?.id,
		})

		// Add full cart items as extra data (stringified to avoid [Object] truncation)
		// Field names use 'Snapshot' suffix to bypass Sentry's server-side data scrubbing
		if (cart?.cartItems?.length) {
			scope.setExtra('vueCartItemsSnapshot', JSON.stringify(cart.cartItems, null, 2))
		}
		if (cart?.certificates?.length) {
			scope.setExtra('vueCertificatesSnapshot', JSON.stringify(cart.certificates, null, 2))
		}
		if (cart?.coupon?.code) {
			scope.setExtra('vueCouponSnapshot', JSON.stringify(cart.coupon, null, 2))
		}

		// Warnings and response data
		if (warnings) {
			scope.setExtra('warnings', warnings)
		}
		if (response) {
			scope.setExtra('response', response)
		}

		// Additional extras
		Object.entries(extra).forEach(([key, value]) => {
			scope.setExtra(key, value)
		})

		// Set level
		scope.setLevel(level)

		// Capture
		if (error instanceof Error) {
			Sentry.captureException(error)
		} else {
			const message = typeof error === 'string' ? error : JSON.stringify(error)
			Sentry.captureMessage(`Cart Error: ${message}`)
		}
	})
}

/**
 * Capture a cart sync error (BigCommerce cart sync failures)
 * This is specifically for when the BC cart sync fails but the order may have been created
 *
 * @param {Error|string} error - The error to capture
 * @param {Object} options - Error options
 * @param {Object} options.cart - Cart object
 * @param {Object} options.user - User object
 * @param {Array} options.warnings - Warning messages
 * @param {boolean} options.orderMayExist - Whether an order may have been created despite the error
 * @param {Object} options.extra - Additional context
 */
export function captureCartSyncError(error, options = {}) {
	const { cart, user, warnings, orderMayExist = false, extra = {} } = options

	captureCartError(error, {
		action: CheckoutAction.SYNC_CART,
		level: orderMayExist ? ErrorLevel.ERROR : ErrorLevel.WARNING,
		cart,
		user,
		warnings,
		extra: {
			orderMayExist,
			...extra,
		},
	})
}

/**
 * Add a breadcrumb for checkout flow tracking
 *
 * @param {string} message - Breadcrumb message
 * @param {Object} options - Breadcrumb options
 * @param {string} options.category - Category (defaults to 'checkout')
 * @param {string} options.level - Level (defaults to 'info')
 * @param {Object} options.data - Additional data
 */
export function addCheckoutBreadcrumb(message, options = {}) {
	const { category = 'checkout', level = 'info', data = {} } = options

	Sentry.addBreadcrumb({
		message,
		category,
		level,
		data,
	})
}

/**
 * Set the current user for Sentry tracking
 *
 * @param {Object} user - User object
 */
export function setCheckoutUser(user) {
	if (user?.id) {
		Sentry.setUser({
			id: user.id,
			email: user.email,
		})
	} else {
		Sentry.setUser(null)
	}
}

/**
 * Create a scoped logger for a specific component
 * Returns helper functions pre-configured with the component name
 *
 * @param {string} component - Component name
 * @param {Object} context - Shared context (store, checkout)
 * @returns {Object} Scoped logger functions
 */
export function createCheckoutLogger(component, context = {}) {
	const { store, checkout } = context

	return {
		error: (error, action, extra = {}) => {
			captureCheckoutError(error, {
				component,
				action,
				store,
				checkout,
				extra,
			})
		},

		warning: (error, action, extra = {}) => {
			captureCheckoutError(error, {
				component,
				action,
				level: ErrorLevel.WARNING,
				store,
				checkout,
				extra,
			})
		},

		shipping: (error, action, extra = {}) => {
			captureShippingError(error, {
				action,
				store,
				checkout,
				extra,
			})
		},

		address: (error, action, addressType, extra = {}) => {
			captureAddressError(error, {
				action,
				addressType,
				store,
				checkout,
				extra,
			})
		},

		payment: (error, paymentMethod, extra = {}) => {
			logCheckoutPaymentFailure({
				error,
				paymentMethod,
				store,
				checkout,
				message: typeof error === 'string' ? error : undefined,
				extra,
				force: extra.force,
			})
		},

		order: (error, action, extra = {}) => {
			captureOrderError(error, {
				action,
				store,
				checkout,
				extra,
			})
		},

		cart: (error, action, extra = {}) => {
			captureCartError(error, {
				action,
				cart: store?.cart,
				user: store?.user,
				extra,
			})
		},

		breadcrumb: (message, data = {}) => {
			addCheckoutBreadcrumb(message, { data })
		},
	}
}

/**
 * Cart/BC messages that are expected user or recovery paths — do not open Sentry issues.
 */
export function shouldSkipCartSentryCapture(message) {
	const text = Array.isArray(message) ? message.join(' ') : String(message || '')
	const patterns = [
		/invalid cart items removed during init/i,
		/variant\s*id\s*is\s*required/i,
		/modifier options/i,
		/missing or incorrect required fields/i,
		/missing required fields/i,
		/coupon code.*not valid/i,
		/corrupted originalcart/i,
		/cart repeatedly failing/i,
		/product with the id of \d+ was not found/i,
		/insufficient stock/i,
		/can only purchase a maximum/i,
		/error undefined updating your cart/i,
	]
	return patterns.some((pattern) => pattern.test(text))
}

const PAYMENT_NOISE_PATTERNS = [
	/aborterror.*paymentrequest/i,
	/paymentrequest.*timed out/i,
	/user closed payment request/i,
	/frame closed before tokenization/i,
	/cannot contact the gateway at this time/i,
]

/**
 * Drop third-party / environmental noise before events reach Sentry.
 * Used from main.js beforeSend — existing unresolved issues still need Archive/Resolve in UI.
 */
export function shouldDropSentryEvent(event) {
	const message = event?.message || event?.exception?.values?.[0]?.value || ''
	const type = event?.exception?.values?.[0]?.type || ''
	const combined = `${type} ${message}`

	const dropPatterns = [
		/AbortError.*paymentrequest/i,
		/paymentrequest.*timed out/i,
		/User closed Payment Request/i,
		/Java object is gone/i,
		/WKWebView API client did not respond/i,
		/zoid destroyed all components/i,
		/Frame closed before tokenization/i,
		/Cannot contact the gateway at this time/i,
		/Failed to fetch.*doubleclick/i,
		/Failed to fetch.*googleadservices/i,
		/Failed to fetch.*googleads\.g/i,
		/Failed to fetch.*datadoghq/i,
		/Failed to fetch.*babylist\.com/i,
		/Failed to fetch.*pinterest/i,
		/NetworkError when attempting to fetch resource/i,
		/Failed to execute 'send' on 'XMLHttpRequest'.*pinterest/i,
		/filter settings/i,
		/drawbridge\.systems/i,
		/Medical \/ Reproduction/i,
		/window\.webkit\.messageHandlers/i,
		/iabjs_unified_bridge/i,
		// Same in-app-browser bridge, read before the webview has injected it (IHH-FRONT-NEW-F2).
		/_META\.IABInnerFrame/i,
		/feature named `pageContext`/i,
		/gorgias-chat/i,
		/render-chat-app/i,
		/Importing a module script failed/i,
		/SecurityError.*localStorage/i,
		/The operation is insecure/i,
		/sandboxed and lacks the 'allow-same-origin'/i,
		/cross-origin frame/i,
		/Can't find variable: contains/i,
		/about:blank.*is not in list/i,
		// Safari network/fetch failure on product/category pages (IHH-FRONT-NEW-1Z) — connectivity/CDN noise, no stack
		/Load failed/i,
		// Third-party checkout script (IHH-FRONT-NEW-BP) — not in repo (analytics/chat widget)
		/No siteData config to use for selectors/i,
		// Google Pay wallet tokenization (IHH-FRONT-NEW-BF) — client-only Braintree parseResponse; users recover via card
		/Google Pay tokenization failed/i,
		// Axios rate limits (status 429). Other 429s stay. The breadcrumb on a later event is kept.
		/Request failed with status code 429/,
	]

	for (const pattern of dropPatterns) {
		if (pattern.test(message) || pattern.test(combined)) return true
	}

	if (/ApiError/i.test(type) && (/<!DOCTYPE html>/i.test(message) || /filter settings/i.test(message))) {
		return true
	}

	const frames = event?.exception?.values?.[0]?.stacktrace?.frames || []
	const hasInAppFrame = frames.some((frame) => frame.in_app)
	if (!hasInAppFrame && /(SecurityError|postMessage|webkit\.messageHandlers)/i.test(combined)) {
		return true
	}

	// No first-party frames: drop served-HTML and third-party SDK recursion noise.
	if (!hasInAppFrame) {
		const framesText = JSON.stringify(frames)
		// HTML error page executed as JS (IHH-FRONT-NEW-BN) — chunk/CDN returned a doctype instead of a script
		if (/<!DOCTYPE html>/i.test(framesText)) return true
		// PayPal/zoid wallet SDK recursion (IHH-FRONT-NEW-9Z, AP) — stack overflow entirely inside /sdk/js
		if (/Maximum call stack size exceeded/i.test(combined) && /(\/sdk\/js|paypalobjects|zoid)/i.test(framesText)) return true
	}

	return false
}

/**
 * Capture a wishlist save/create failure. These were previously shown only in
 * SweetAlert, so they never reached Sentry.
 */
export function captureWishlistError(error, options = {}) {
	const { action = 'saveWishlist', warnings, store, extra = {} } = options
	const warningText = Array.isArray(warnings) ? warnings.join(' ') : warnings || ''
	const message = typeof error === 'string' ? error : warningText || 'Wishlist save failed'

	Sentry.withScope((scope) => {
		scope.setTag('component', 'wishlist')
		scope.setTag('action', action)
		scope.setLevel('error')
		if (store?.user?.id) scope.setTag('userId', String(store.user.id))
		if (store?.user?.bcId) scope.setTag('bcId', String(store.user.bcId))
		if (warningText) scope.setExtra('warnings', warnings)
		Object.entries(extra).forEach(([key, value]) => {
			if (value !== undefined) scope.setExtra(key, value)
		})
		Sentry.captureMessage(message)
	})
}

// Re-export Sentry for edge cases where direct access is needed
export { Sentry }
